import {
  needsSensitiveConfirm,
  sensitiveLevelsOf,
  type CatalogueEntry,
  type SensitiveLevel,
} from "@/lib/authz/fieldExclusion";

/** How many sensitive questions one save may ask before it gives up (B2 F2, audit r2 A3). */
export const SENSITIVE_RECHECK_ROUNDS = 3;

/**
 * `ok` = go on; `failed` = a catalogue read failed; `declined` = cancelled;
 * `capped` = still changing after `SENSITIVE_RECHECK_ROUNDS` questions — its
 * own refusal text (B2 F3, audit r3 R3-LOW-3).
 */
export type SensitiveRecheckOutcome = "ok" | "failed" | "declined" | "capped";

/**
 * The sensitive confirms of one editor session: key → the levels the admin
 * let it into Visible at. A confirm holds for its level only — a key let in
 * as sensitive (or of an unknown level) that reads as ART9 later — or at any
 * other level — is asked about again (B2 F3, audit r3 R3-LOW-2).
 */
export type SensitiveConfirmations = Map<string, Set<SensitiveLevel>>;

/** Records a confirm of `keys` at the levels `catalogue` — the one the question was asked on — gives them. */
export function noteSensitiveConfirmed(
  confirmed: SensitiveConfirmations,
  keys: readonly string[],
  catalogue: readonly CatalogueEntry[],
): void {
  for (const [k, level] of sensitiveLevelsOf(keys, catalogue)) {
    const levels = confirmed.get(k);
    if (levels) levels.add(level);
    else confirmed.set(k, new Set([level]));
  }
}

/**
 * B2 F1 (S-MED-1) + F2 (audit r2 A3): the sensitive re-check an "All except"
 * save runs — the role editor's and the preview bar's. `first` is the
 * uncached catalogue read made right before (null = it failed); `added` the
 * keys this session ADDED to the list. Every added key that is sensitive,
 * access data or of an unknown level and was not confirmed at that level yet
 * is asked about. A confirm takes a while: a field raised while the question
 * was open would slip in — so after every confirm the catalogue is read AGAIN
 * (`reread`, uncached) and anything that turned sensitive (or changed level)
 * meanwhile is asked about too, until nothing new comes up. At most
 * `SENSITIVE_RECHECK_ROUNDS` questions; a catalogue still changing after that
 * is refused (`capped`). A failed read refuses a save that adds keys; a save
 * that adds nothing asks nothing. `confirmed` collects what the admin let in.
 */
export async function recheckSensitiveAdds({
  added,
  first,
  reread,
  ask,
  confirmed,
}: {
  added: readonly string[];
  first: readonly CatalogueEntry[] | null;
  reread: () => Promise<readonly CatalogueEntry[] | null>;
  ask: (keys: string[]) => Promise<boolean>;
  confirmed: SensitiveConfirmations;
}): Promise<SensitiveRecheckOutcome> {
  if (added.length === 0) return "ok";
  let fresh = first;
  for (let round = 0; ; round += 1) {
    if (!fresh) return "failed";
    const needs = needsSensitiveConfirm([...added], fresh);
    const levels = sensitiveLevelsOf(needs, fresh);
    const open = needs.filter((k) => !confirmed.get(k)?.has(levels.get(k) ?? "UNKNOWN"));
    if (open.length === 0) return "ok";
    if (round >= SENSITIVE_RECHECK_ROUNDS) return "capped";
    if (!(await ask(open))) return "declined";
    noteSensitiveConfirmed(confirmed, open, fresh);
    fresh = await reread();
  }
}
